import {openDialog, showNotification, Tabs, type TabProps} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useGetTenantV2Query} from '@dms/api/base';
import {useDeleteCustomClosingDayMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError, useCountry} from '@dms/shared';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {CustomClosingDayModal} from './components/CustomClosingDayModal';
import {PublicClosingDayModal} from './components/PublicClosingDayModal';
import {CLOSING_DAY_DIALOG_ID, CLOSING_DAY_GRID_CODE} from './utils/constants';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR + 1] as const;

const handleAddClosingDay = () => {
  openDialog(<CustomClosingDayModal mode="createCustom" />, {
    title: i18n.t('page.settings.labels.addClosingDay'),
    id: CLOSING_DAY_DIALOG_ID,
    size: 'small',
  });
};

export function HolidayCalendar() {
  const [activeYear, setActiveYear] = useState(CURRENT_YEAR);
  const {data: tenant} = useGetTenantV2Query();
  const {getCountryName} = useCountry();
  const [deleteCustomClosingDay] = useDeleteCustomClosingDayMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {year: activeYear.toString()}]);

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const closingDayId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('editPublic', () => {
        openDialog(<PublicClosingDayModal closingDayId={closingDayId} />, {
          title: i18n.t('page.settings.labels.editPublicHoliday'),
          id: CLOSING_DAY_DIALOG_ID,
          size: 'small',
        });
      })
      .with('editCustom', () => {
        openDialog(<CustomClosingDayModal mode="updateCustom" closingDayId={closingDayId} />, {
          title: i18n.t('page.settings.labels.editCustomHoliday'),
          id: CLOSING_DAY_DIALOG_ID,
          size: 'small',
        });
      })
      .with('deleteCustom', () =>
        deleteCustomClosingDay({closingDayId})
          .unwrap()
          .then(() => {
            refreshData();
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
          })
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const yearTabs: TabProps[] = YEARS.map((year) => ({
    id: year.toString(),
    title: year.toString(),
    content: (
      <DmsDataGrid
        gridCode={CLOSING_DAY_GRID_CODE}
        data-testid={testIds.settings.holidayCalendar('grid')}
        actionCallback={actionCallback}
        autoHeight
        queryModifier={queryModifier}
      />
    ),
  }));

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('page.settings.labels.holidayCalendar'),
        subtitle: i18n.t('page.settings.labels.holidayCalendarDescription'),
        actions: [
          {
            type: 'button',
            leftIcon: 'content/add_circle',
            title: i18n.t('page.settings.actions.addClosingDay'),
            variant: 'primary',
            onClick: handleAddClosingDay,
            'data-testid': testIds.settings.holidayCalendar('addClosingDay'),
          },
        ],
      }}
      data-testid={testIds.settings.holidayCalendar('page')}
    >
      <VStack spacing={6}>
        <VStack spacing={1}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.labels.country')}
          </Text>
          <Text>{getCountryName(tenant?.country)}</Text>
          <Text size="xSmall" color="tertiary">
            {i18n.t('page.settings.labels.basedOnTenantSettings')}
          </Text>
        </VStack>

        <Tabs
          variant="condensed"
          tabs={yearTabs}
          activeTabId={activeYear.toString()}
          onChange={(id) => setActiveYear(Number(id))}
          data-testid={testIds.settings.holidayCalendar('yearTabs')}
          isLazy
        />
      </VStack>
    </SettingsTemplate>
  );
}
